import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { type FC, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { appPaths } from "../../app/appPaths.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectTeammate } from "../teammate/teammateSlice.js";

/**
 * URL segment that stands in for a real playbook ID in stable links (e.g.
 * marketing emails, which can't know a customer's playbook IDs):
 *
 *   https://app.alpharun.com/performance/playbooks/default/teammates
 *
 * Playbook IDs are UUIDs, so this segment can never collide with a real one.
 */
export const DEFAULT_PLAYBOOK_URL_SEGMENT = "default";

const PLACEHOLDER_PATH_PREFIX = appPaths.playbook.build({
  playbookId: DEFAULT_PLAYBOOK_URL_SEGMENT,
});

const CenteredMessage: FC<{ children: string }> = ({ children }) => (
  <div className="flex h-full w-full items-center justify-center">
    <div className="text-lg text-gray-600">{children}</div>
  </div>
);

/**
 * Resolves the teammate's default playbook and redirects to its URL,
 * preserving `restPath` plus the current query string and hash. The default is
 * the teammate's last selected playbook when it still exists, falling back to
 * the first playbook — the same choice made when landing on the app root.
 *
 * A loader stays on screen until the navigation lands, so resolving the
 * playbook never flashes an empty page.
 */
export const DefaultPlaybookRedirect: FC<{ restPath?: string }> = ({
  restPath = "",
}) => {
  const { search, hash } = useLocation();
  const navigate = useNavigate();
  const {
    data: minimalPlaybooks,
    isLoading,
    error,
  } = useAllMinimalPlaybooksQuery();

  const lastSelectedPlaybookId =
    useAppSelector(selectTeammate)?.settings.preferences
      .team_performance_playbook_id;

  const firstPlaybookId = minimalPlaybooks?.[0]?.id;
  const targetPlaybookId =
    lastSelectedPlaybookId &&
    minimalPlaybooks?.some((p) => p.id === lastSelectedPlaybookId)
      ? lastSelectedPlaybookId
      : firstPlaybookId;

  useEffect(() => {
    if (targetPlaybookId === undefined) {
      return;
    }
    navigate(
      `${appPaths.playbook.build({ playbookId: targetPlaybookId })}${restPath}${search}${hash}`,
      { replace: true },
    );
  }, [hash, navigate, restPath, search, targetPlaybookId]);

  if (error) {
    return (
      <CenteredMessage>
        Error loading playbooks. Please try again.
      </CenteredMessage>
    );
  }

  if (!isLoading && firstPlaybookId === undefined) {
    return (
      <CenteredMessage>
        No playbooks found. Please create a playbook first.
      </CenteredMessage>
    );
  }

  // Also covers the frame(s) between resolving the playbook and the redirect
  // landing: the effect above navigates, this keeps the page from going blank.
  return (
    <div className="flex h-full w-full items-center justify-center">
      <Loader style={LoaderStyle.ZOOMIES} />
    </div>
  );
};

/**
 * The parent of every playbook-scoped route: it renders the subtree untouched,
 * except for default-playbook placeholder URLs, which it resolves to the
 * teammate's default playbook and redirects:
 *
 *   /performance/playbooks/default/teammates
 *     -> /performance/playbooks/<default playbook id>/teammates
 *
 * A parent route (rather than a route of its own) because route ranking would
 * otherwise send these URLs to the deeper `:playbookId` routes with the
 * literal string "default" as the playbook ID.
 *
 * Resolving it renders a loader in the page content, with the app chrome
 * around it — so the chrome, unlike the routes below it, does render while the
 * URL still says "default". Chrome that needs the playbook itself must read it
 * through `usePlaybookIdParam`, which yields `undefined` for the placeholder;
 * only link building may pass the raw `:playbookId` through, since a
 * placeholder link resolves here in turn.
 */
export const DefaultPlaybookGate: FC = () => {
  const { playbookId } = useParams();
  const { pathname } = useLocation();

  if (playbookId !== DEFAULT_PLAYBOOK_URL_SEGMENT) {
    return <Outlet />;
  }

  // Everything after the placeholder segment (e.g. "" or "/teammates") is
  // carried over to the resolved playbook's URL.
  return (
    <DefaultPlaybookRedirect
      restPath={pathname.slice(PLACEHOLDER_PATH_PREFIX.length)}
    />
  );
};
